import {
	Alert,
	Box,
	Button,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";

import { type Control, useFormState } from "react-hook-form";

import type { InspectionFormValues } from "../../models/InspectionForm";
import type { useInspectionForm } from "../hooks/useInspectionForm";
import { ReadingsValidator } from "../hooks/useLegDeltas";
import { DimensionReadingsTable } from "./inspection-form/DimensionReadingsTable";
import {
	InspectionCommentsField,
	InspectionFormFields,
	InspectionIdentityFields,
} from "./inspection-form/InspectionFormFields";

// Subscribes only to these errors; reading formState.errors in the form hook
// re-rendered the whole layout on every onChange validation.
const FormFooterMessage = ({
	control,
	hasPendingChanges,
}: {
	control: Control<InspectionFormValues>;
	hasPendingChanges: boolean;
}) => {
	const { errors } = useFormState({ control });
	const errorMessages = [
		errors.root?.context?.message,
		errors.readings?.message,
	].filter((message): message is string => typeof message === "string");
	if (errorMessages.length > 0)
		return (
			<Alert
				severity="error"
				sx={{
					py: 0,
					minHeight: 32,
					"& .MuiAlert-icon, & .MuiAlert-message": { py: 0.5 },
				}}
			>
				{errorMessages.map((message) => (
					<Typography key={message} variant="body2">
						{message}
					</Typography>
				))}
			</Alert>
		);
	return hasPendingChanges ? null : (
		<Typography variant="caption" color="text.secondary">
			No changes to save
		</Typography>
	);
};

interface InspectionFormProps {
	inspectionForm: ReturnType<typeof useInspectionForm>;
	layout?: "split" | "stacked";
}

export const InspectionForm = ({
	inspectionForm,
	layout = "split",
}: InspectionFormProps) => {
	const isWide = useMediaQuery((theme) => theme.breakpoints.up("xl"));
	const isStacked = layout === "stacked" || !isWide;
	const {
		assetId,
		editing,
		saving,
		blocked,
		onCancelEdit,
		onSectionChange,
		form: { control, getValues, reset, setValue, getFieldState },
		serverTimeZone,
		selectedSectionId,
		inspectionTime,
		specQuery,
		specReady,
		spec,
		specItems,
		itemKeys,
		targetLbsPerFoot,
		minLbsPerFoot,
		maxLbsPerFoot,
		legDeltaMax,
		firstFieldRef,
		hasResolvedCurrentSectionRef,
		hasPendingChanges,
		canSave,
		handleSubmit,
	} = inspectionForm;

	return (
		<Stack
			component="form"
			onSubmit={handleSubmit}
			noValidate
			data-test-id="inspection-form"
			spacing={1.5}
			sx={{ minHeight: "100%" }}
		>
			<ReadingsValidator
				control={control}
				items={specItems}
				getReadings={() => getValues("readings")}
			/>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					{editing
						? `Edit sample #${editing.ProfileInspectionId}`
						: "New inspection"}
				</Typography>
			</Stack>
			{selectedSectionId !== null && inspectionTime !== null && (
				<>
					{specQuery.isError && (
						<Alert severity="error" role="alert">
							The specification for the selected sample time could not be
							loaded.
						</Alert>
					)}
					{specReady && spec === null && (
						<Alert severity="warning" role="status">
							No confirmed specification is effective at the selected sample
							time.
						</Alert>
					)}
				</>
			)}
			<Box
				component="fieldset"
				disabled={blocked}
				sx={{
					border: 0,
					p: 0,
					m: 0,
					minWidth: 0,
					display: "flex",
					flexDirection: "column",
					flex: "1 0 auto",
				}}
			>
				{isStacked ? (
					<Box
						sx={{
							display: "flex",
							flex: "1 0 auto",
							flexDirection: "column",
							gap: 1.5,
						}}
					>
						<InspectionIdentityFields
							orientation="row"
							assetId={assetId}
							sectionId={selectedSectionId}
							control={control}
							editing={editing}
							saving={saving}
							serverTimeZone={serverTimeZone}
							itemKeys={itemKeys}
							targetLbsPerFoot={targetLbsPerFoot}
							minLbsPerFoot={minLbsPerFoot}
							maxLbsPerFoot={maxLbsPerFoot}
							getValues={getValues}
							reset={reset}
							setValue={setValue}
							getFieldState={getFieldState}
							firstFieldRef={firstFieldRef}
							hasResolvedCurrentSectionRef={hasResolvedCurrentSectionRef}
							onSectionChange={onSectionChange}
						/>

						<Box sx={{ flexShrink: 0 }}>
							<DimensionReadingsTable
								dimensions={specItems}
								compact={layout === "stacked"}
								control={control}
								isEditingInspection={editing !== null}
								legDeltaFooter={{ max: legDeltaMax }}
							/>
						</Box>

						<InspectionCommentsField control={control} fill />
					</Box>
				) : (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "minmax(0, 1fr)",
								xl: "minmax(220px, 1fr) minmax(0, 2.25fr)",
							},
							gap: 1.5,
							alignItems: "start",
							flex: { xs: "0 0 auto", lg: "1 0 auto" },
						}}
					>
						<InspectionFormFields
							assetId={assetId}
							sectionId={selectedSectionId}
							control={control}
							editing={editing}
							saving={saving}
							serverTimeZone={serverTimeZone}
							itemKeys={itemKeys}
							targetLbsPerFoot={targetLbsPerFoot}
							minLbsPerFoot={minLbsPerFoot}
							maxLbsPerFoot={maxLbsPerFoot}
							dimensions={specItems}
							legDeltaMax={legDeltaMax}
							getValues={getValues}
							reset={reset}
							setValue={setValue}
							getFieldState={getFieldState}
							firstFieldRef={firstFieldRef}
							hasResolvedCurrentSectionRef={hasResolvedCurrentSectionRef}
							onSectionChange={onSectionChange}
						/>

						<DimensionReadingsTable
							dimensions={specItems}
							control={control}
							isEditingInspection={editing !== null}
						/>
					</Box>
				)}
			</Box>

			<Box
				sx={{
					display: "flex",
					flexGrow: 0,
					flexShrink: 0,
					alignItems: { xs: "stretch", lg: "flex-end" },
					minHeight: 0,
				}}
			>
				<Stack
					direction="row"
					spacing={1}
					alignItems="flex-end"
					justifyContent="flex-end"
					sx={{ width: "100%", minWidth: 0, flexWrap: "wrap", gap: 1 }}
				>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							minHeight: 32,
							flexBasis: { xs: "100%", sm: 0 },
						}}
					>
						<FormFooterMessage
							control={control}
							hasPendingChanges={hasPendingChanges}
						/>
					</Box>
					<Stack direction="row" spacing={1} alignItems="center">
						{editing && (
							<Button
								type="button"
								data-test-id="cancel-inspection-edit-button"
								size="small"
								onClick={onCancelEdit}
							>
								Cancel
							</Button>
						)}
						<Button
							type="submit"
							variant="contained"
							data-test-id="save-inspection-button"
							disabled={!canSave}
							sx={{ flexShrink: 0 }}
						>
							{editing ? "Update" : "Save inspection"}
						</Button>
					</Stack>
				</Stack>
			</Box>
		</Stack>
	);
};
